<script setup lang="ts">
// Sierra's sheet: anchored at the top of a dim scrim, min(600px, 100%) wide (820 when
// `wide`), with a sticky head holding the title and the `actions` slot (Cancel, then
// the primary action last), the body in the default slot, and an optional `foot`.
// Focus moves into it on open, is kept inside it while open (Sierra has no trap), and
// goes back where it was on close. Emits `close` on the scrim, × or Escape.
import { nextTick, onBeforeUnmount, ref, useId, watch } from 'vue';

const props = withDefaults(defineProps<{ open?: boolean; title?: string; wide?: boolean; closable?: boolean }>(), {
  open: true,
  title: '',
  wide: false,
  closable: true,
});
const emit = defineEmits<{ close: [] }>();
const id = useId();
const sheet = ref<HTMLElement | null>(null);
let returnTo: HTMLElement | null = null;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const focusables = (): HTMLElement[] => [...(sheet.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter((el) => el.offsetParent !== null);

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.closable) {
    event.stopPropagation();
    emit('close');
    return;
  }
  if (event.key !== 'Tab' || sheet.value === null) return;
  const items = focusables();
  const first = items[0];
  const last = items[items.length - 1];
  if (first === undefined || last === undefined) {
    event.preventDefault();
    sheet.value.focus();
  } else if (event.shiftKey && (document.activeElement === first || document.activeElement === sheet.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  } else if (!sheet.value.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      document.addEventListener('keydown', onKey);
      await nextTick();
      // The first field, else the first control, else the sheet itself.
      const field = sheet.value?.querySelector<HTMLElement>('.itc-sheet__body input:not([disabled]), .itc-sheet__body select:not([disabled]), .itc-sheet__body textarea:not([disabled])');
      (field ?? focusables()[0] ?? sheet.value)?.focus();
    } else {
      document.removeEventListener('keydown', onKey);
      returnTo?.focus();
      returnTo = null;
    }
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="itc-sheet-scrim" @click.self="closable && emit('close')">
      <div
        ref="sheet"
        class="itc-sheet"
        :class="{ 'itc-sheet--wide': wide }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="title ? `${id}-title` : undefined"
        tabindex="-1"
      >
        <header class="itc-sheet__head">
          <h2 :id="`${id}-title`" class="itc-sheet__title">{{ title }}</h2>
          <div class="itc-sheet__actions">
            <slot name="actions" />
            <button v-if="closable && !$slots.actions" type="button" class="itc-btn itc-btn--ghost itc-btn--sm" aria-label="Close" @click="emit('close')">×</button>
          </div>
        </header>
        <div class="itc-sheet__body"><slot /></div>
        <footer v-if="$slots.foot" class="itc-sheet__foot"><slot name="foot" /></footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.itc-sheet-scrim {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: calc(16px + env(safe-area-inset-top, 0px)) 16px 16px;
  overflow-y: auto;
  background: var(--itc-scrim);
}
.itc-sheet {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: min(600px, 100%);
  max-height: calc(100dvh - 32px);
  padding: 0 18px 18px;
  overflow-y: auto;
  background: var(--itc-panel);
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-sheet);
  outline: none;
}
.itc-sheet--wide {
  width: min(820px, 100%);
}
.itc-sheet__head {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 -18px;
  padding: 12px 18px;
  background: var(--itc-panel);
  border-bottom: 1px solid var(--itc-line);
}
.itc-sheet__title {
  margin: 0;
  font-family: var(--itc-font-display);
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-wrap: balance;
}
.itc-sheet__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}
.itc-sheet__body {
  color: var(--itc-fg);
}
.itc-sheet__foot {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
}
</style>
